import type { Ref } from 'react';
import { memo, useEffect, useMemo, useRef } from 'react';

import { PlayerSetup } from '~/interfaces';
import { VideoState } from '~/modules/elements/video-player/index';
import styles from '~/modules/game-engine/drawing/styles';
import GameState from '~/modules/game-engine/game-state/game-state';
import { getDurationTicks } from '~/routes/game/singing/game-overlay/helpers/duration-ticks';
import { atSecond } from '~/routes/game/singing/game-overlay/helpers/song-position';

interface Props {
  players: PlayerSetup[];
  currentStatus: VideoState;
  duration: number;
  /** Room kept clear on the right, as a CSS length, so the bar stops short of the scores. */
  rightInset?: string;
}

function DurationBar({ players, duration, rightInset }: Props) {
  const barFillRef = useRef<HTMLDivElement>(null);
  const nowRef = useRef<HTMLSpanElement>(null);
  const elapsedRef = useRef<HTMLSpanElement>(null);
  const remainingRef = useRef<HTMLSpanElement>(null);
  const beatLength = GameState.getSongBeatLength();
  const song = GameState.getSong()!;

  const durationMs = duration * 1000;

  const ticks = useMemo(
    () =>
      getDurationTicks({
        tracks: song.tracks
          .filter((_, index) => players.find((player) => player.track === index))
          .map(({ sections }) => sections),
        beatLength,
        gap: song.gap,
        durationMs,
      }),
    [song, beatLength, players, durationMs],
  );

  useEffect(() => {
    // Written straight to the DOM rather than through state: the overlay already re-renders every
    // frame, and a clock that ticks does not need to add renders of its own.
    const update = () => {
      if (!duration || !barFillRef.current) return;

      const currentTime = GameState.getCurrentTime(false);
      if (!currentTime) return;

      const played = Math.min(1, currentTime / durationMs);
      barFillRef.current.style.transform = `scaleX(${played})`;
      if (nowRef.current) nowRef.current.style.left = `${played * 100}%`;
      if (elapsedRef.current) elapsedRef.current.textContent = atSecond(currentTime);
      if (remainingRef.current) remainingRef.current.textContent = `−${atSecond(durationMs - currentTime)}`;
    };

    const interval = setInterval(update, 50); // ~60fps

    return () => clearInterval(interval);
  }, [duration, durationMs]);

  if (!duration) return null;

  return (
    <div
      className="absolute top-1/2 left-0 z-10 flex w-full -translate-y-1/2 items-center gap-4 px-10"
      style={rightInset ? { paddingRight: rightInset } : undefined}>
      <Time ref={elapsedRef} name="elapsed" />
      <div className="relative h-1.5 flex-1 rounded-[0.1875rem] bg-black/50 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]">
        <BarFill ref={barFillRef} />
        {ticks.map((position) => (
          <span
            key={position}
            className="absolute -top-1 h-3.5 w-0.5"
            style={{ left: `${position * 100}%`, background: styles.colors.text.active }}
          />
        ))}
        <span
          ref={nowRef}
          className="absolute -top-[0.3125rem] -ml-2 size-4 rounded-full bg-white shadow-[0_0_0_3px_rgba(0,0,0,0.5)]"
          style={{ left: 0 }}
          data-test="duration-bar-now"
        />
      </div>
      <Time ref={remainingRef} name="remaining" />
    </div>
  );
}

const Time = ({ ref, name }: { ref: Ref<HTMLSpanElement>; name: 'elapsed' | 'remaining' }) => (
  <span
    ref={ref}
    className="stroke-text text-[1.125rem] font-bold text-white/70 tabular-nums"
    data-test={`duration-bar-${name}`}
  />
);

const BarFill = ({ ref }: { ref: Ref<HTMLDivElement> }) => (
  <div ref={ref} className="absolute inset-y-0 left-0 w-full origin-left scale-x-0 rounded-[0.1875rem] bg-white/70" />
);

export default memo(DurationBar);
